import { useEffect, useState } from 'react';

interface VersionInfo {
  service: string;
  packageName: string;
  version: string;
  gitSha: string | null;
  buildTime: string | null;
}

interface BuildBadgeProps {
  /** URL for the frontend version.json. Default: "./version.json" (resolves
   *  to the current Vite base, so each app fetches its own). */
  frontendVersionUrl?: string;
  /** URL for the subsystem backend's version endpoint
   *  (e.g. "/api/monitoring/version"). Omit for shell-only / FE-only apps. */
  backendVersionUrl?: string;
  /** Compact rendering for collapsed sidebar — a short sha only. */
  compact?: boolean;
}

async function safeFetchVersion(url: string): Promise<VersionInfo | null> {
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return (await res.json()) as VersionInfo;
  } catch {
    return null;
  }
}

function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : '—';
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function buildTitle(label: string, info: VersionInfo | 'down' | null): string | undefined {
  if (info === 'down') return `${label}: backend unreachable`;
  if (!info) return undefined;
  const lines = [`${info.packageName}@${info.version}`, info.gitSha ?? 'sha: unknown'];
  if (info.buildTime) lines.push(`built ${info.buildTime}`);
  return lines.join('\n');
}

/**
 * Tiny version badge for the sidebar footer.
 * Shows frontend (always) and optionally backend version.
 * Backend "offline" state surfaces in amber so deployment regressions
 * are visible at a glance.
 */
export function BuildBadge({
  frontendVersionUrl = './version.json',
  backendVersionUrl,
  compact = false,
}: BuildBadgeProps) {
  const [fe, setFe] = useState<VersionInfo | null>(null);
  const [be, setBe] = useState<VersionInfo | 'down' | null>(null);

  useEffect(() => {
    let cancelled = false;
    safeFetchVersion(frontendVersionUrl).then((v) => {
      if (!cancelled) setFe(v);
    });
    if (backendVersionUrl) {
      safeFetchVersion(backendVersionUrl).then((v) => {
        if (!cancelled) setBe(v ?? 'down');
      });
    } else {
      setBe(null);
    }
    return () => {
      cancelled = true;
    };
  }, [frontendVersionUrl, backendVersionUrl]);

  if (compact) {
    return (
      <span
        title={buildTitle('FE', fe)}
        className="text-[10px] text-muted-foreground/70 select-none"
      >
        {shortSha(fe?.gitSha)}
      </span>
    );
  }

  return (
    <div className="text-[10px] leading-tight text-muted-foreground/80 select-none space-y-0.5">
      <div title={buildTitle('FE', fe)}>
        <span className="font-medium">FE</span> v{fe?.version ?? '—'} · {shortSha(fe?.gitSha)}
        {fe?.buildTime && <> · {relativeTime(fe.buildTime)}</>}
      </div>
      {backendVersionUrl && (
        <div
          title={buildTitle('BE', be)}
          className={be === 'down' ? 'text-amber-700 dark:text-amber-400' : ''}
        >
          {be === 'down' ? (
            <>
              <span className="font-medium">BE</span> offline
            </>
          ) : be ? (
            <>
              <span className="font-medium">BE</span> v{be.version} · {shortSha(be.gitSha)}
              {be.buildTime && <> · {relativeTime(be.buildTime)}</>}
            </>
          ) : (
            <>
              <span className="font-medium">BE</span> …
            </>
          )}
        </div>
      )}
    </div>
  );
}
